import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus, Star } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/dates";

/**
 * What people say about the software (T15-M4).
 *
 * **A record, not a queue** — the one structural difference from
 * `AdminTickets`, which this screen otherwise copies. A ticket is a debt
 * somebody is waiting on, so that queue sorts oldest-first and every row is
 * something to act on. Nobody is owed a reply here; what an admin wants is what
 * people are saying now, so this sorts newest-first and has no state to change.
 * There is nothing to click into: the row IS the whole record.
 *
 * **A list of cards and not a table**, and the reason is copy. Nine columns
 * would need nine headers, and T15 ships exactly seven approved strings — one
 * of which is this page's title. Values are data, not copy: a role, a path, a
 * width and a locale read perfectly well as a context line under the comment,
 * which is also the better shape for free text that varies from empty to a
 * thousand characters.
 *
 * `common.noResults`, `common.prev` and `common.next` are existing keys. This
 * screen invents no copy at all.
 *
 * Deliberately no metrics strip. AdminTickets has one because DANY.md §2.7
 * closes its improvement cycle on it; the brief for this one says "sin métricas
 * elaboradas", and an average star rating across a launch cohort is a number
 * that would get quoted and mean nothing.
 */

const PAGE = 25;

function Stars({ value }) {
  return (
    <span
      className="flex gap-0.5"
      // The number is what a screen reader should hear, not five icon names.
      // No copy: a bare digit is not a string anyone translated.
      aria-label={String(value)}
      data-testid={`feedback-rating-${value}`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={16}
          aria-hidden="true"
          className={n <= value ? "text-hive-ink fill-hive" : "text-sand"}
        />
      ))}
    </span>
  );
}

/** The context columns, as one line. Empty parts drop out rather than showing
 *  a dash: absence is a real state (0022) and a placeholder would read as a
 *  value somebody chose. */
function Context({ row }) {
  const parts = [
    row.user_email,
    row.role,
    row.route,
    row.viewport_width ? `${row.viewport_width}px` : null,
    row.locale,
  ].filter(Boolean);
  return (
    <p className="text-[11px] text-smoke font-mono mt-2 break-words" data-testid="feedback-context">
      {parts.join("  ·  ")}
    </p>
  );
}

export default function AdminFeedback() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/feedback?limit=${PAGE}&skip=${skip}`);
      setRows(data.feedback || []);
      setTotal(data.total || 0);
      setError("");
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    }
  }, [skip, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-feedback">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquarePlus size={22} className="text-hive-ink" />
          <h1 className="font-display text-3xl font-bold text-worker">
            {t("feedback.adminTitle")}
          </h1>
        </div>

        {error && (
          <p className="text-danger text-sm mb-4" data-testid="feedback-error">{error}</p>
        )}

        {/* `min-w-0` for the same reason DashboardLayout's `<main>` carries it
            (T13-D0): a long route or a long unbroken comment must shrink this
            card, never widen the page. */}
        <div className="card-base max-w-3xl min-w-0" data-testid="feedback-list">
          {rows.length === 0 ? (
            <p className="p-6 text-smoke text-sm" data-testid="feedback-empty">
              {t("common.noResults")}
            </p>
          ) : (
            rows.map((row, i) => (
              <div
                key={`${row.created_at}-${i}`}
                className="p-4 border-t border-sand first:border-0 min-w-0"
                data-testid="feedback-row"
              >
                <div className="flex items-start justify-between gap-3">
                  <Stars value={row.rating} />
                  <span className="text-xs text-smoke whitespace-nowrap">
                    {formatDate(row.created_at)}
                  </span>
                </div>
                {row.comment && (
                  <p
                    className="text-sm text-worker mt-2 break-words whitespace-pre-wrap"
                    data-testid="feedback-comment-text"
                  >
                    {row.comment}
                  </p>
                )}
                <div className="flex items-end justify-between gap-3 min-w-0">
                  <Context row={row} />
                  {/* The one context value that earns a chip: it says WHICH
                      door this came through, and the two doors mean different
                      things about how motivated the person was. */}
                  <span className="chip shrink-0 mt-2" data-testid="feedback-source">
                    {row.source}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {total > PAGE && (
          <div className="flex items-center gap-2 mt-4 max-w-3xl">
            <button
              type="button"
              className="fx-btn fx-btn--ghost"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE))}
              data-testid="feedback-prev"
            >
              {t("common.prev")}
            </button>
            <button
              type="button"
              className="fx-btn fx-btn--ghost"
              disabled={skip + PAGE >= total}
              onClick={() => setSkip(skip + PAGE)}
              data-testid="feedback-next"
            >
              {t("common.next")}
            </button>
            <span className="text-xs text-smoke font-mono ml-auto" data-testid="feedback-count">
              {skip + 1}–{Math.min(skip + PAGE, total)} {t("common.of")} {total}
            </span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
